import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Pop Protector upsell toggle.
 *
 * A standalone add-on control: clicking it adds (or removes) the linked
 * Acrylic Pop Protector variant as its own cart line, independent of the
 * main product's Add to cart button. Uses the theme's standard cart events
 * so the cart icon bubble, cart drawer, and any other listener stay in sync
 * exactly as they would for a normal add-to-cart, and reuses the theme's
 * existing fly-to-cart animation for a small, consistent visual cue.
 *
 * The protector line is tagged with a `_pop_protector_for` property equal to
 * the main product's ID, so the toggle can find "its" line again on reload
 * without relying on browser storage.
 */
class PopProtectorToggle extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-protector-toggle-btn]');
    if (!this.button) return;

    this.button.addEventListener('click', this.handleClick);
    this.#syncFromCart();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.handleClick);
  }

  /** @param {MouseEvent} _event */
  handleClick = async (_event) => {
    if (!this.button || this.button.disabled) return;

    const pressed = this.button.getAttribute('aria-pressed') === 'true';
    this.button.disabled = true;

    try {
      if (pressed) {
        await this.#removeProtector();
      } else {
        await this.#addProtector();
      }
    } catch (error) {
      console.error('[pop-protector-toggle]', error);
    } finally {
      this.button.disabled = false;
    }
  };

  get #variantId() {
    return this.dataset.protectorVariantId;
  }

  get #mainProductId() {
    return this.dataset.mainProductId;
  }

  async #addProtector() {
    const variantId = this.#variantId;
    if (!variantId) return;

    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'pop-protector-upsell',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(window.Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: 1,
              properties: {
                _pop_protector_for: this.#mainProductId || '',
                _pop_protector_upgrade: this.dataset.mainProductTitle || '',
              },
            },
          ],
        }),
      });

      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.message || 'Could not add the protector upgrade');
      }

      const cart = await this.#fetchCart();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'pop-protector-upsell', itemCount: cart.item_count },
      });

      this.#animateFlyToCart();
      this.#setPressed(true);
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(new CartErrorEvent({ error: error.message, code: 'INVALID' }));
      throw error;
    }
  }

  async #removeProtector() {
    const cart = await this.#fetchCart();
    const line = this.#findLine(cart);

    if (!line) {
      this.#setPressed(false);
      return;
    }

    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'remove',
        context: 'pop-protector-upsell',
        lines: [{ merchandiseId: this.#variantId, quantity: 0 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(`${window.Theme.routes.cart_change_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: line.key, quantity: 0 }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Could not remove the protector upgrade');
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
        detail: { items: data.items, source: 'pop-protector-upsell', itemCount: data.item_count },
      });

      this.#setPressed(false);
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(new CartErrorEvent({ error: error.message, code: 'INVALID' }));
      throw error;
    }
  }

  /**
   * Small, subtle visual cue that the upgrade was added — reuses the theme's
   * existing fly-to-cart element (same one the main Add to cart button uses),
   * in its "quick" (smaller/faster) variant. No product image: just a plain
   * dot in the theme's foreground color, since there's no product media
   * handy here and a plain dot keeps it deliberately understated.
   */
  #animateFlyToCart() {
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    if (!cartIcon || !this.button) return;

    const flyToCartElement = document.createElement('fly-to-cart');
    flyToCartElement.classList.add('fly-to-cart--quick');
    flyToCartElement.source = this.button;
    flyToCartElement.destination = cartIcon;
    document.body.appendChild(flyToCartElement);
  }

  /**
   * @param {{items: Array<any>}} cart
   */
  #findLine(cart) {
    return cart.items.find(
      (item) =>
        String(item.variant_id) === String(this.#variantId) &&
        item.properties &&
        String(item.properties._pop_protector_for) === String(this.#mainProductId)
    );
  }

  async #fetchCart() {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    return response.json();
  }

  async #syncFromCart() {
    try {
      const cart = await this.#fetchCart();
      const line = this.#findLine(cart);
      this.#setPressed(!!line);
    } catch (_error) {
      // Leave the default unpressed state if the cart can't be read yet.
    }
  }

  /** @param {boolean} pressed */
  #setPressed(pressed) {
    if (!this.button) return;
    this.button.setAttribute('aria-pressed', String(pressed));
  }
}

if (!customElements.get('pop-protector-toggle')) {
  customElements.define('pop-protector-toggle', PopProtectorToggle);
}
